import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { xadmApi } from '../../api/xadm.js';
import { Spinner, ErrorBox, EmptyBox } from '../../components/ui.js';
import { LineChart } from '../../components/LineChart.js';
import { useUrlUpdate } from '../../lib/hooks.js';
import { num } from '../../lib/format.js';

/**
 * 신규가입자 — 원본 /home/bananapartner/xadm/new_member_list.php.
 *
 * 요일별 가입 합계와 PC·모바일 및 가입 경로 데이터를 그대로 유지하면서,
 * 최근 변화와 시간대별 상세를 빠르게 탐색하는 대시보드로 구성한다.
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const WEEKDAY_NAMES = WEEKDAYS.map((day) => `${day}요일`);

function hourLabel(hour: number) {
  return `${String(hour).padStart(2, '0')}:00`;
}

function signed(value: number) {
  return `${value > 0 ? '+' : ''}${num(value)}`;
}

export function MembersPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const weekdayParam = params.get('weekday');
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [pinnedDate, setPinnedDate] = useState<string | null>(null);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openDate = hoverDate ?? pinnedDate;

  const days = useQuery({
    queryKey: ['xadm-member-days', weekdayParam],
    queryFn: () => xadmApi.memberDays(weekdayParam === null ? undefined : Number(weekdayParam)),
  });

  const hours = useQuery({
    queryKey: ['xadm-member-hours', openDate],
    queryFn: () => xadmApi.memberHours(openDate!),
    enabled: openDate !== null,
  });

  const selectedWeekday = weekdayParam === null ? (days.data?.weekday ?? null) : Number(weekdayParam);
  const openDay = days.data?.days.find((day) => day.date === openDate) ?? null;
  const summary = useMemo(() => {
    const rows = days.data?.days ?? [];
    const latest = rows[0] ?? null;
    const previous = rows[1] ?? null;
    const recentFour = rows.slice(0, 4);
    const trend = [...rows.slice(0, 12)].reverse();
    const delta = latest && previous ? latest.total - previous.total : null;
    return {
      dayCount: rows.length,
      latest,
      delta,
      deltaRate: delta === null || !previous?.total ? null : (delta / previous.total) * 100,
      average: recentFour.length === 0
        ? 0
        : Math.round(recentFour.reduce((sum, row) => sum + row.total, 0) / recentFour.length),
      mobileRate: latest?.total ? (latest.mobile / latest.total) * 100 : 0,
      peak: trend.length === 0 ? null : trend.reduce((max, row) => row.total > max.total ? row : max),
      trend,
    };
  }, [days.data]);

  const trendLabels = summary.trend.map((day) => day.date);
  const trendSeries = [
    { id: 'total', label: '전체', points: summary.trend.map((day) => ({ x: day.date, y: day.total })) },
    { id: 'pc', label: 'PC', points: summary.trend.map((day) => ({ x: day.date, y: day.pc })) },
    { id: 'mobile', label: '모바일', points: summary.trend.map((day) => ({ x: day.date, y: day.mobile })) },
  ];

  const cancelHoverClose = () => {
    if (hoverCloseTimer.current !== null) clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = null;
  };

  const previewDate = (date: string) => {
    cancelHoverClose();
    setHoverDate(date);
  };

  const schedulePreviewClose = () => {
    cancelHoverClose();
    hoverCloseTimer.current = setTimeout(() => setHoverDate(null), 180);
  };

  const togglePinnedDate = (date: string) => {
    cancelHoverClose();
    setHoverDate(null);
    setPinnedDate((current) => current === date ? null : date);
  };

  const selectWeekday = (weekday: number) => {
    setHoverDate(null);
    setPinnedDate(null);
    update({ weekday });
  };

  useEffect(() => () => cancelHoverClose(), []);

  return (
    <section className="visit-page member-page">
      <header className="page-head visit-page__head">
        <div>
          <h1 className="page-title">신규가입자 통계</h1>
          <p className="page-desc">같은 요일의 신규가입 변화를 비교하고 디바이스·가입 경로·시간대까지 탐색합니다.</p>
        </div>
        <span className="visit-page__source"><i aria-hidden="true" /> HOURLY MEMBER LOG</span>
      </header>

      <nav className="visit-weekdays" aria-label="조회 요일">
        <span className="visit-weekdays__label">요일 선택</span>
        <div className="visit-weekdays__buttons">
          {WEEKDAYS.map((label, weekday) => (
            <button
              key={weekday}
              type="button"
              className={weekday === selectedWeekday ? 'visit-weekday visit-weekday--active' : 'visit-weekday'}
              aria-pressed={weekday === selectedWeekday}
              title={WEEKDAY_NAMES[weekday]}
              onClick={() => selectWeekday(weekday)}
            >
              {label}
            </button>
          ))}
        </div>
        <p>선택한 요일끼리 신규가입 성과를 비교합니다.</p>
      </nav>

      {!days.isLoading && !days.isError && summary.dayCount > 0 && (
        <>
          <div className="visit-summary" aria-label="신규가입 핵심 지표">
            <article className="visit-summary__item visit-summary__item--primary">
              <span>최근 신규가입</span>
              <strong>{num(summary.latest?.total ?? 0)}</strong>
              <small>{`기준 ${summary.latest?.date}`} · {WEEKDAY_NAMES[selectedWeekday ?? 0]}</small>
            </article>
            <article className={`visit-summary__item visit-summary__item--delta ${summary.delta !== null && summary.delta < 0 ? 'visit-summary__item--down' : ''}`}>
              <span>직전 동일 요일 대비</span>
              <strong>{summary.delta === null ? '—' : signed(summary.delta)}</strong>
              <small>{summary.deltaRate === null ? '비교 데이터 없음' : `${summary.deltaRate > 0 ? '+' : ''}${summary.deltaRate.toFixed(1)}% 변화`}</small>
            </article>
            <article className="visit-summary__item member-summary__average">
              <span>최근 4회 평균</span>
              <strong>{num(summary.average)}</strong>
              <small>같은 요일 기준 신규가입</small>
            </article>
            <article className="visit-summary__item visit-summary__item--mobile">
              <span>최근 모바일 비중</span>
              <strong>{summary.mobileRate.toFixed(1)}%</strong>
              <small>모바일 {num(summary.latest?.mobile ?? 0)} · PC {num(summary.latest?.pc ?? 0)}</small>
            </article>
          </div>

          <section className="visit-trend member-trend" aria-label="최근 신규가입 추이">
            <header className="visit-trend__head">
              <div>
                <span>TREND</span>
                <h2>최근 {summary.trend.length}회 신규가입 추이</h2>
                <p>선택한 {WEEKDAY_NAMES[selectedWeekday ?? 0]}의 전체·PC·모바일 가입을 비교합니다.</p>
              </div>
              {summary.peak && (
                <div className="visit-trend__peak">
                  <span>최근 최고</span>
                  <strong>{num(summary.peak.total)}</strong>
                  <small>{`기준 ${summary.peak.date}`}</small>
                </div>
              )}
            </header>
            <LineChart series={trendSeries} labels={trendLabels} height={220} />
          </section>
        </>
      )}

      <div className="visit-list-head">
        <div>
          <h2>{selectedWeekday === null ? '신규가입 기록' : `${WEEKDAY_NAMES[selectedWeekday]} 신규가입 기록`}</h2>
          <p>날짜 행에 마우스를 올리면 오른쪽에서 시간대별 디바이스와 가입 경로를 확인할 수 있습니다.</p>
        </div>
        {!days.isLoading && !days.isError && <span>{num(summary.dayCount)}개 날짜</span>}
      </div>

      {days.isLoading ? (
        <Spinner />
      ) : days.isError ? (
        <ErrorBox error={days.error} />
      ) : days.data!.days.length === 0 ? (
        <EmptyBox />
      ) : (
        <div className="visit-table-wrap">
          <table className="table member-table">
            <thead>
              <tr>
                <th aria-label="상세 열기" />
                <th>일자</th>
                <th>요일</th>
                <th>디바이스</th>
                <th>가입 유형</th>
                <th>소셜 채널</th>
                <th>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {days.data!.days.map((day) => {
                const isOpen = openDate === day.date;
                return (
                  <tr
                    key={day.date}
                    className={isOpen ? 'table__row--clickable visit-table__row visit-table__row--open' : 'table__row--clickable visit-table__row'}
                    onMouseEnter={() => previewDate(day.date)}
                    onMouseLeave={schedulePreviewClose}
                    onClick={() => togglePinnedDate(day.date)}
                  >
                    <td className="visit-table__toggle">
                      <button
                        type="button"
                        aria-label={`${day.date} 시간대 상세 ${pinnedDate === day.date ? '닫기' : '고정해서 열기'}`}
                        aria-expanded={isOpen}
                        onClick={(event) => {
                          event.stopPropagation();
                          togglePinnedDate(day.date);
                        }}
                      >
                        <span aria-hidden="true">›</span>
                      </button>
                    </td>
                    <td className="visit-table__date">{day.date}</td>
                    <td><span className={`visit-week-badge visit-week-badge--${day.weekday}`}>{WEEKDAYS[day.weekday]}</span></td>
                    <td><DeviceMetric pc={day.pc} mobile={day.mobile} /></td>
                    <td><JoinTypeMetric banana={day.banana} slim={day.slim} normal={day.normal} social={day.social} /></td>
                    <td><SocialMetric naver={day.naver} google={day.google} twitter={day.twitter} /></td>
                    <td className="td-right visit-table__total">{num(day.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openDate && openDay && (
        <aside
          className="visit-preview member-preview"
          aria-label={`${openDay.date} 시간대별 신규가입`}
          onMouseEnter={cancelHoverClose}
          onMouseLeave={schedulePreviewClose}
        >
          <section className="visit-detail">
            <header className="visit-detail__head">
              <div>
                <span>{WEEKDAY_NAMES[openDay.weekday]}</span>
                <h3>{openDay.date} 시간대별 신규가입</h3>
              </div>
              <div className="visit-detail__actions">
                <strong>일 가입 {num(openDay.total)}</strong>
                <button
                  type="button"
                  aria-label="시간대별 신규가입 닫기"
                  onClick={() => {
                    setHoverDate(null);
                    setPinnedDate(null);
                  }}
                >
                  ×
                </button>
              </div>
            </header>
            {hours.isLoading ? (
              <Spinner />
            ) : hours.isError ? (
              <ErrorBox error={hours.error} />
            ) : (hours.data?.hours.length ?? 0) === 0 ? (
              <EmptyBox />
            ) : (
              <div className="visit-detail__table-wrap">
                <table className="table table--nested member-hour-table">
                  <thead>
                    <tr>
                      <th>시간대</th>
                      <th>디바이스</th>
                      <th>가입 유형</th>
                      <th>소셜 채널</th>
                      <th>TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(hours.data?.hours ?? []).map((hour) => (
                      <tr key={hour.hour}>
                        <td><span className="visit-hour">{hourLabel(hour.hour)}</span></td>
                        <td><DeviceMetric pc={hour.pc} mobile={hour.mobile} /></td>
                        <td><JoinTypeMetric banana={hour.banana} slim={hour.slim} normal={hour.normal} social={hour.social} /></td>
                        <td><SocialMetric naver={hour.naver} google={hour.google} twitter={hour.twitter} /></td>
                        <td className="td-right member-hour-table__total">{num(hour.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </aside>
      )}
    </section>
  );
}

function DeviceMetric({ pc, mobile }: { pc: number; mobile: number }) {
  return (
    <div className="member-metric member-metric--device">
      <span><small>PC</small><strong>{num(pc)}</strong></span>
      <span><small>M</small><strong>{num(mobile)}</strong></span>
    </div>
  );
}

function JoinTypeMetric({ banana, slim, normal, social }: { banana: number; slim: number; normal: number; social: number }) {
  return (
    <div className="member-metric member-metric--types">
      <span><small>바나나</small><strong>{num(banana)}</strong></span>
      <span><small>미니</small><strong>{num(slim)}</strong></span>
      <span><small>일반</small><strong>{num(normal)}</strong></span>
      <span><small>소셜</small><strong>{num(social)}</strong></span>
    </div>
  );
}

function SocialMetric({ naver, google, twitter }: { naver: number; google: number; twitter: number }) {
  return (
    <div className="member-metric member-metric--social">
      <span><small>네이버</small><strong>{num(naver)}</strong></span>
      <span><small>구글</small><strong>{num(google)}</strong></span>
      <span><small>트위터</small><strong>{num(twitter)}</strong></span>
    </div>
  );
}
